import axios from 'axios';
import React, { useEffect, useState } from 'react'
import API_ROUTES from '../../../../api';
import moment from 'moment';
import {
    Button,
    Modal,
    Row,
    Image,
    Descriptions,
    Form,
    Col,
    Radio,
    Input,
    Select,
    Tag,
    Space,
    Card,
    Typography,
    Divider
} from 'antd';
import {
    UserOutlined,
    SafetyCertificateOutlined,
    AuditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;

export default function UserInfo({ userId, onUserLoaded, onUpdated }) {

    const [accountForm] = Form.useForm();
    const [userInfo, setUserInfo] = useState({})
    const [accountLoading, setAccountLoading] = useState(false)
    const [roleLoading, setRoleLoading] = useState(false)

    const fetchUserInfo = () => {
        axios.post(API_ROUTES.api_get_user_info, null, {
            params: {
                id: userId || localStorage.getItem('clickedUser'),
            },
            headers: {
                "Authorization": localStorage.getItem('userInfo')
            }
        })
            .then(function (response) {
                if (response.data.code === 500) {
                    Modal.error({ content: response.data.msg });
                } else {
                    setUserInfo(response.data.data)
                    onUserLoaded?.(response.data.data)
                }
            })
            .catch(function (error) {
                Modal.error({ content: error.response?.data?.msgDes || "Failed to load user info" });
            })
    }

    useEffect(() => {
        fetchUserInfo();
    }, [userId]);

    const formatDateTime = (value) => value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';

    const accountStatusTag = (status) => {
        const config = {
            0: { color: 'error', text: 'Disabled / Rejected' },
            1: { color: 'success', text: 'Enabled' },
            2: { color: 'warning', text: 'Pending review' },
        }[status] || { color: 'default', text: 'Unknown' };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const renderText = (value, strong = false) => value ? <Text strong={strong}>{value}</Text> : <Text type="secondary">-</Text>;

    const accountItems = [
        {
            key: 'id',
            label: 'User ID',
            children: renderText(userInfo.id, true),
        },
        {
            key: 'email',
            label: 'Email Address',
            children: renderText(userInfo.email, true),
        },
        {
            key: 'name',
            label: 'Full Name',
            children: renderText(userInfo.realName || userInfo.name, true),
        },
        {
            key: 'org',
            label: 'Organization',
            children: renderText(userInfo.org),
        },
        {
            key: 'mobile',
            label: 'Contact Number',
            children: renderText(userInfo.mobile),
        },
        {
            key: 'role',
            label: 'User Role',
            children: userInfo.manage === 1 ? <Tag color="gold">Admin</Tag> : <Tag color="blue">User</Tag>,
        },
    ];

    const auditItems = [
        {
            key: 'accountStatus',
            label: 'Account Status',
            children: accountStatusTag(userInfo.status),
        },
        {
            key: 'currentCount',
            label: 'Today Submissions',
            children: userInfo.currentCount ?? 0,
        },
        {
            key: 'totalCount',
            label: 'Total Submission Quota',
            children: userInfo.totalCount ?? '-',
        },
        {
            key: 'lastGetTime',
            label: 'Last Submission Reset',
            children: formatDateTime(userInfo.lastGetTime),
        },
        {
            key: 'updateTime',
            label: 'Last Updated',
            children: formatDateTime(userInfo.updateTime),
        },
        {
            key: 'updateUser',
            label: 'Updated By',
            children: renderText(userInfo.updateUserName || userInfo.updateUser),
        },
    ];

    const accountReviewDisabled = false;
    const affiliationName = userInfo.org || 'the provided institution';

    // 审核拒绝原因快捷模板：按钮只显示英文短标签，填充内容用于发送英文审核邮件
    const accountRejectionTemplates = [
        {
            label: 'No proof document',
            value: 'Thank you for registering for a researcher account. We were unable to complete the review because no valid identity or affiliation verification document was provided.\n\nPlease upload a document that helps verify your real name and institutional affiliation, such as a student/staff ID card, employment certificate, institutional letter, or other official proof. You may mask sensitive personal numbers, but your name and institution should remain visible.\n\nAfter the document is updated, your account can be reviewed again.',
        },
        {
            label: 'Unclear document',
            value: 'Thank you for registering for a researcher account. We were unable to complete the review because the uploaded verification document was unclear, incomplete, or could not be properly read.\n\nPlease upload a clearer document that shows your name and institutional affiliation. Sensitive personal numbers may be masked, but the information required for verification should remain visible.\n\nAfter the document is updated, your account can be reviewed again.',
        },
        {
            label: 'Information mismatch',
            value: 'Thank you for registering for a researcher account. We were unable to approve your application because the information provided in the registration form does not match the uploaded verification document.\n\nPlease check that your full name, institution/organization, and verification document are consistent. If you are using a personal email address, please make sure that the uploaded document clearly verifies your institutional affiliation.\n\nAfter the information is corrected, your account can be reviewed again.',
        },
        {
            label: 'Affiliation not confirmed',
            value: `Thank you for registering for a researcher account. We noticed that your application was submitted using a personal email address. This is acceptable only when your institutional affiliation can be verified through the uploaded document.\n\nBased on the current submission, we were unable to confirm your affiliation with ${affiliationName}. Please upload a valid student/staff ID card, employment certificate, institutional letter, or other official proof showing your name and institution.\n\nAfter the document is updated, your account can be reviewed again.`,
        },
    ];

    const submitAudit = (apiRoute, values, setSubmitLoading, successText) => {
        setSubmitLoading(true);
        axios.post(apiRoute, {
            id: userId || localStorage.getItem('clickedUser'),
            auditResult: values.auditResult,
            opinion: values.opinion
        }, {
            headers: { "Authorization": localStorage.getItem('userInfo') }
        })
            .then(function (response) {
                if (response.data.code === 500) {
                    Modal.error({ content: response.data.msg });
                } else {
                    Modal.success({ content: successText });
                    fetchUserInfo();
                    onUpdated?.();
                }
            })
            .catch(function (error) {
                Modal.error({ content: error.response?.data?.msgDes || "Submission failed" });
            })
            .finally(() => setSubmitLoading(false));
    };

    // 管理端角色切换：只调整 manage 字段，详情和列表在成功后同步刷新
    const updateUserRole = (nextManage) => {
        if (Number(userInfo.manage) === nextManage) {
            return;
        }
        Modal.confirm({
            title: 'Confirm role change',
            content: `Change ${userInfo.email || 'this user'} to ${nextManage === 1 ? 'Admin' : 'User'}?`,
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                setRoleLoading(true);
                axios.post(API_ROUTES.api_manage_update, {
                    id: userId || localStorage.getItem('clickedUser'),
                    manage: nextManage,
                }, {
                    headers: { "Authorization": localStorage.getItem('userInfo') }
                })
                    .then(function (response) {
                        if (response.data.code === 500) {
                            Modal.error({ content: response.data.msg });
                        } else {
                            Modal.success({ content: 'User role updated successfully!' });
                            fetchUserInfo();
                            onUpdated?.();
                        }
                    })
                    .catch(function (error) {
                        Modal.error({ content: error.response?.data?.msgDes || "Role update failed" });
                    })
                    .finally(() => setRoleLoading(false));
            }
        });
    };

    const renderDecisionForm = ({
        apiRoute,
        disabled,
        loading,
        setSubmitLoading,
        approveText,
        rejectText,
        notesPlaceholder,
        successText,
        form,
        quickTemplates,
    }) => (
        <Form
            form={form}
            layout="vertical"
            onFinish={(values) => submitAudit(apiRoute, values, setSubmitLoading, successText)}
        >
            <Form.Item
                name="auditResult"
                label="Decision"
                rules={[{ required: true, message: 'Please select a decision' }]}
            >
                <Radio.Group disabled={disabled} style={{ width: '100%' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Radio.Button value={1} style={{ width: '100%', textAlign: 'center', borderRadius: '6px' }}>
                            <Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> {approveText}</Space>
                        </Radio.Button>
                        <Radio.Button value={2} style={{ width: '100%', textAlign: 'center', borderRadius: '6px' }}>
                            <Space><CloseCircleOutlined style={{ color: '#f5222d' }} /> {rejectText}</Space>
                        </Radio.Button>
                    </Space>
                </Radio.Group>
            </Form.Item>

            {quickTemplates?.length > 0 && (
                <Form.Item label="Quick rejection notes">
                    <Space size={[8, 8]} wrap>
                        {quickTemplates.map((template) => (
                            <Button
                                key={template.label}
                                htmlType="button"
                                size="small"
                                disabled={disabled}
                                onClick={() => {
                                    // 点击快捷原因时自动切换为拒绝，减少管理员重复录入
                                    form?.setFieldsValue({
                                        auditResult: 2,
                                        opinion: template.value,
                                    });
                                }}
                            >
                                {template.label}
                            </Button>
                        ))}
                    </Space>
                </Form.Item>
            )}

            <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.auditResult !== currentValues.auditResult}
            >
                {({ getFieldValue }) => (
                    <Form.Item
                        name="opinion"
                        label="Auditor's Notes"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (getFieldValue('auditResult') === 2 && !value?.trim()) {
                                        return Promise.reject(new Error('Please enter a rejection reason.'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <TextArea
                            rows={5}
                            placeholder={notesPlaceholder}
                            disabled={disabled}
                            style={{ borderRadius: '8px' }}
                        />
                    </Form.Item>
                )}
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
                <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    disabled={disabled}
                    style={{ height: '44px', borderRadius: '8px', fontWeight: '600' }}
                >
                    {disabled ? 'Decision Unavailable' : 'Confirm Decision'}
                </Button>
            </Form.Item>
        </Form>
    );

    const renderImageGroup = (value, title) => {
        if (!value) {
            return <Text type="secondary">Not provided</Text>;
        }
        return (
            <Image.PreviewGroup>
                <Space size={12} wrap>
                    {String(value).split(';').filter(Boolean).map((url, idx) => (
                        <Image
                            key={`${title}-${idx}`}
                            width={116}
                            height={116}
                            src={url}
                            style={{ objectFit: 'cover', borderRadius: '8px', border: '1px solid #d9edf2' }}
                        />
                    ))}
                </Space>
            </Image.PreviewGroup>
        );
    };

    const proofImages = [
        {
            key: 'organizationProof',
            label: 'Affiliation Verification Document',
            span: 2,
            children: renderImageGroup(userInfo.studentCertificate, 'Affiliation Verification Document'),
        }
    ];

    return (
        <div style={{ padding: '0 20px' }}>
            <Row gutter={24}>
                <Col span={16}>
                    <Card
                        title={<Space><UserOutlined /> User Verification Details</Space>}
                        bordered={false}
                        className="detail-card"
                        style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                    >
                        <Descriptions
                            title={<Text type="secondary" style={{ fontSize: '14px' }}>Account profile and verification information</Text>}
                            layout="horizontal"
                            bordered
                            column={2}
                            items={accountItems}
                        />

                        <Divider orientation="left" style={{ marginTop: '32px' }}>
                            <Space><AuditOutlined /> Account Review & Usage</Space>
                        </Divider>

                        <Descriptions
                            bordered
                            column={2}
                            items={auditItems}
                        />

                        <Divider orientation="left" style={{ marginTop: '32px' }}>
                            <Space><SafetyCertificateOutlined /> Proof Documents</Space>
                        </Divider>

                        <Descriptions
                            bordered
                            column={2}
                            items={proofImages}
                        />
                    </Card>
                </Col>

                <Col span={8}>
                    <Card
                        title={<Space><AuditOutlined /> Account Review</Space>}
                        bordered={false}
                        style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                    >
                        {renderDecisionForm({
                            apiRoute: API_ROUTES.api_audit_image,
                            disabled: accountReviewDisabled,
                            loading: accountLoading,
                            setSubmitLoading: setAccountLoading,
                            approveText: 'Approve Account',
                            rejectText: 'Reject Account',
                            notesPlaceholder: 'Enter reason for account rejection or approval notes...',
                            successText: 'Account review submitted successfully!',
                            form: accountForm,
                            quickTemplates: accountRejectionTemplates,
                        })}
                    </Card>

                    <Card
                        title={<Space><UserOutlined /> Role Management</Space>}
                        bordered={false}
                        style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginTop: 16 }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            <Text type="secondary">Current role</Text>
                            <Select
                                value={Number(userInfo.manage) === 1 ? 1 : 0}
                                loading={roleLoading}
                                onChange={updateUserRole}
                                style={{ width: '100%' }}
                                options={[
                                    { value: 0, label: 'User' },
                                    { value: 1, label: 'Admin' },
                                ]}
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Role changes take effect after the user signs in again.
                            </Text>
                        </Space>
                    </Card>

                </Col>
            </Row>
        </div>
    )
}
